import {
  type BasicData,
  baseFreeFormatTypes,
  type DeletedElementsData,
  type DiagramPayload,
  type DraftData,
  type GraphicData,
  metaModelMap,
  type PayloadDraftEntry,
  SupportedMetamodels,
  type UUID,
  type View,
} from '@bizzdesign/modeler/models';
import {
  convertPayloadToFreeFormatMetamodel,
  getTypeMetadataFromMetaModelTerm,
} from '@bizzdesign/modeler/utils-canvazz';
import {
  type BaseObjectDto,
  type BaseRelationDto,
} from '@bizzdesign/sdk-knowledgegraph';
import {
  convertBaseRelationToSemanticalRelationData,
  convertObjectToSemanticalData,
} from './sdk-to-semantics.utils';
import { convertFromSdkObjectTypeToMetaModelTerm } from './sdk-types.utils';
// Note: for v2 this is used for read-only mode

/**
 * Converts from SDK published view data into payload data for the modeller.
 * Extracts the referenced elements and creates the corresponding data definitions.
 * Also wraps the graphical data into an object so it matches payload structure.
 * @param viewObject stored view data of a published view
 * @param convertToFreeformat flag to indicate a non-Freeformat view should be converted to Freeformat
 * @returns payload data
 */
export function convertFromSDKPublishedViewData(
  viewObject: View,
  convertToFreeformat = false,
): DiagramPayload {
  const content: object = viewObject.content ?? {};
  const referencedObjects: BaseObjectDto[] = viewObject.objectReferences ?? [];
  const referencedRelations: BaseRelationDto[] =
    viewObject.relationReferences ?? [];

  let semanticIds: string[] = [];

  // the same regardless of metamodel
  const graphics: PayloadDraftEntry[] = Object.values(content).map((object) => {
    const metaModelTerm = convertFromSdkObjectTypeToMetaModelTerm(
      object.metaModelTerm ?? object.vocabularyTerm,
    );

    object.metaModelTerm = metaModelTerm;
    return {
      [object.id]: {
        ...object,
      },
    };
  });

  const deletedIds = determineDeletedIds(
    referencedObjects,
    referencedRelations,
    graphics,
  );

  // build the semanticIds
  semanticIds = [
    ...referencedObjects.map((object) => object.id ?? ''),
    ...referencedRelations.map((link) => link.id ?? ''),
    ...deletedIds,
  ];

  let result: PayloadDraftEntry | null = null;
  const firstGraphicalElement = Object.values(graphics[0] ?? {})[0];
  let currentMetamodelName = getViewMetamodelName(
    viewObject,
    firstGraphicalElement,
  );
  if (currentMetamodelName.includes('FreeFormat')) {
    result = {
      ...graphics.reduce((acc, item) => ({ ...acc, ...item }), {}),
    };
  } else {
    // assume it is BDCore/ArchiMate
    result = buildSemanticalPayloadEntryFromSDK(
      referencedObjects,
      referencedRelations,
      graphics,
      deletedIds,
    );

    if (convertToFreeformat) {
      result = convertPayloadToFreeFormatMetamodel(result, (semanticId) =>
        semanticIds.includes(semanticId),
      );
      semanticIds = [];
      currentMetamodelName = SupportedMetamodels.FREEFORMAT;
    }
  }

  const viewData: DiagramPayload = {
    payload: result,
    stateToken: '',
    fromRepoElements: semanticIds,
    metamodelMetadata: {
      currentMetamodelName,
    },
  };

  return viewData;
}

export function buildSemanticalPayloadEntryFromSDK(
  referencedObjects: BaseObjectDto[],
  referencedRelations: BaseRelationDto[],
  graphics: PayloadDraftEntry[],
  deletedIds: string[],
): PayloadDraftEntry {
  const compatibleGraphics = handleMetadataBackwardsComp(graphics);
  const missingElements = handleDeletedElements(compatibleGraphics, deletedIds);

  const entities: object[] = referencedObjects.map((object) => {
    const objectId = object.id ?? '';
    return {
      [objectId]: convertObjectToSemanticalData(object) as DraftData,
    };
  });
  const links: object[] = referencedRelations.map((relation) => {
    const relationData = convertBaseRelationToSemanticalRelationData(relation);
    const relationId = relation.id ?? '';
    return {
      [relationId]: {
        ...relationData,
      },
    };
  });
  const deletedEntities: object[] = missingElements.missedEntities.map(
    (entity) => ({ [entity.id]: entity }),
  );
  const deletedLinks: object[] = missingElements.missedRelations.map(
    (link) => ({ [link.id]: link }),
  );

  return {
    ...missingElements.updatedGraphics.reduce(
      (acc, item) => ({ ...acc, ...item }),
      {},
    ),
    ...entities.reduce((acc, item) => ({ ...acc, ...item }), {}),
    ...deletedEntities.reduce((acc, item) => ({ ...acc, ...item }), {}),
    ...links.reduce((acc, item) => ({ ...acc, ...item }), {}),
    ...deletedLinks.reduce((acc, item) => ({ ...acc, ...item }), {}),
  };
}

export function handleMetadataBackwardsComp(
  graphics: PayloadDraftEntry[],
): PayloadDraftEntry[] {
  return graphics.map<PayloadDraftEntry>((graphicPayload) => {
    const data = Object.values(graphicPayload)[0];
    const extData = (data as any)['externalData'];
    return {
      [data.id]: {
        ...data,
        metadata: extData
          ? {
              ...(data as GraphicData).metadata,
              external: extData,
            }
          : (data as GraphicData).metadata,
      },
    };
  });
}

export function determineDeletedIds(
  referencedObjects: BaseObjectDto[],
  referencedRelations: BaseRelationDto[],
  graphics: PayloadDraftEntry[],
): string[] {
  const graphicValues = graphics.map((graphicData) => {
    const value = Object.values(graphicData);
    return value[0];
  }) as GraphicData[];
  const referencedObjectIds = new Set(referencedObjects.map((obj) => obj.id));
  const referencedRelationsIds = new Set(
    referencedRelations.map((rel) => rel.id),
  );
  const missingObjIds = graphicValues
    .filter(
      (graphic) =>
        !graphic.sourceId &&
        !baseFreeFormatTypes.includes(graphic.typeName) &&
        graphic.semanticsId &&
        !referencedObjectIds.has(graphic.semanticsId as UUID),
    )
    .map((obj) => obj.semanticsId);

  const missingRelationIds = graphicValues
    .filter(
      (graphic) =>
        !!graphic.sourceId &&
        !baseFreeFormatTypes.includes(graphic.typeName) &&
        graphic.semanticsId &&
        !referencedRelationsIds.has(graphic.semanticsId as UUID),
    )
    .map((relation) => relation.semanticsId);
  const combinedIds = [...missingObjIds, ...missingRelationIds];
  const result = [];
  for (const id of combinedIds) {
    if (!id) continue;
    result.push(id);
  }
  return result;
}

export function handleDeletedElements(
  graphics: PayloadDraftEntry[],
  deletedIds: string[],
): DeletedElementsData {
  const graphicValues = graphics.map((graphicData) => {
    const value = Object.values(graphicData);
    return value[0];
  }) as GraphicData[];
  let updatedGraphics = [...graphics];
  let missedEntities: BasicData[] = [];
  let missedRelations: BasicData[] = [];
  const missingMetamodelObjects = graphicValues.filter(
    (graphic) =>
      !graphic.sourceId &&
      graphic.semanticsId &&
      deletedIds.includes(graphic.semanticsId as UUID),
  );
  const missingMetamodelRelations = graphicValues.filter(
    (graphic) =>
      !!graphic.sourceId &&
      graphic.semanticsId &&
      deletedIds.includes(graphic.semanticsId as UUID),
  );
  for (const objGraphic of missingMetamodelObjects) {
    missedEntities.push(buildBasicData(objGraphic, false));
    const newGraphicData: PayloadDraftEntry = {
      [objGraphic.id]: {
        ...objGraphic,
        metadata: {
          ...(objGraphic.metadata ?? {}),
          isDeletedFromRepo: true,
        },
      },
    };
    const index = updatedGraphics.findIndex((data) => data[objGraphic.id]);
    updatedGraphics.splice(index, 1, newGraphicData);
  }
  for (const relGraphic of missingMetamodelRelations) {
    const sourceId = graphicValues.find(
      (graphic) => graphic.id === relGraphic.sourceId,
    )?.semanticsId;
    const targetId = graphicValues.find(
      (graphic) => graphic.id === relGraphic.targetId,
    )?.semanticsId;
    missedRelations.push({
      ...buildBasicData(relGraphic, true),
      sourceId: sourceId ?? '',
      targetId: targetId ?? '',
    });
    const newGraphicData: PayloadDraftEntry = {
      [relGraphic.id]: {
        ...relGraphic,
        metadata: {
          ...(relGraphic.metadata ?? {}),
          isDeletedFromRepo: true,
        },
      },
    };
    const index = updatedGraphics.findIndex((data) => data[relGraphic.id]);
    updatedGraphics.splice(index, 1, newGraphicData);
  }

  return {
    missedEntities,
    missedRelations,
    updatedGraphics,
  };
}

function buildBasicData(graphic: GraphicData, isRelation: boolean): BasicData {
  const objectNameFallback = graphic.semanticsId ?? '';
  const nameFallback = isRelation ? '' : objectNameFallback;
  return {
    version: '1.0',
    id: (graphic.semanticsId ?? '') as UUID,
    name: {
      en: graphic.name?.en ?? nameFallback,
    },
    typeName: graphic.typeName,
    metaModelTerm: graphic.metaModelTerm,
    parentId: 'm0',
    elementKind: 'model',
  };
}

export function getViewMetamodelName(
  view: View,
  firstGraphic: DraftData | undefined,
): SupportedMetamodels {
  let metaModel: SupportedMetamodels | undefined = view.metaModel
    ? metaModelMap.get(view.metaModel)
    : undefined;
  // TODO: remove second condition once we remove ArchiMate views with FreeFormat-typed graphical content
  if (!!metaModel && metaModel !== SupportedMetamodels.ARCHIMATE)
    return metaModel;
  metaModel = SupportedMetamodels.BDCORE;
  if (!firstGraphic) return metaModel;
  // graphic metaModelTerm => MetamodelName:TypeName
  const { metamodelName } = getTypeMetadataFromMetaModelTerm(
    firstGraphic?.metaModelTerm,
  );
  if (!!metamodelName) {
    metaModel = metamodelName;
  }
  return metaModel;
}
